// Organic Ranking & Visibility Tracker.
//
// The name the PRD settles on, and the one thing it is emphatic about: Amazon
// exposes organic search position through no API, so nothing here displays or
// implies a rank. What ships is visibility.
//
// Cards first, one per sub-feature, each carrying its PRD reference — this
// surface exists to be checked against that document. Opening a card drops
// into the tab that answers it.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLink } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './Visibility.module.css';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import FeatureCards from './components/FeatureCards';
import Keywords from './components/Keywords';
import Score from './components/Score';
import Traffic from './components/Traffic';
import ProductTraffic from './components/ProductTraffic';
import { NotConnectedError, getCatalogue, getKeywords, getOverview, getProductTraffic,
         getScores, getTraffic } from './api';
import { num } from './format';

// The card key doubles as the tab id, so opening a card lands on its surface.
const TABS = [
  { id: 'keywords', label: 'Keyword performance', ref: 'SF1 · SF4' },
  { id: 'sessions', label: 'Traffic', ref: 'SF2 · SF3' },
  { id: 'score', label: 'Visibility score', ref: 'SF5' },
];

export default function Visibility() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { connected, loading: checking } = useAmazonStatus(token);

  const [tab, setTab] = useState('sessions');
  const [period, setPeriod] = useState('WEEK');
  const [startDate, setStartDate] = useState('');
  const [windowOptions, setWindowOptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [traffic, setTraffic] = useState(null);
  // SF2/SF3's per-ASIN graph. Its own state and its own fetch: a seller looks
  // at one product without disturbing the table they were reading.
  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [scores, setScores] = useState(null);
  const [keywords, setKeywords] = useState(null);

  const [catalogue, setCatalogue] = useState([]);
  const [asin, setAsin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(true);
    else setError(e.message);
  }, []);

  // The cards describe the window, so they follow the window and nothing else.
  useEffect(() => {
    if (!token || !connected) return undefined;
    let live = true;
    getOverview(token, { period, start_date: startDate })
      .then((r) => {
        if (!live) return;
        setOverview(r);
        setWindowOptions(r.window_options || []);
        if (r.start_date && !startDate) setStartDate(r.start_date);
      })
      .catch((e) => { if (live) fail(e); });
    return () => { live = false; };
  }, [token, connected, period, startDate, fail]);

  useEffect(() => {
    if (!token || !connected) return;
    getCatalogue(token).then(setCatalogue).catch(() => {});
  }, [token, connected]);

  const load = useCallback(async () => {
    if (!token || !connected) return;
    setLoading(true);
    setError(null);
    try {
      const args = { period, start_date: startDate };
      if (tab === 'sessions') setTraffic(await getTraffic(token, args));
      else if (tab === 'score') setScores(await getScores(token, args));
      else if (tab === 'keywords' && asin) setKeywords(await getKeywords(token, { ...args, asin }));
    } catch (e) { fail(e); } finally { setLoading(false); }
  }, [token, connected, tab, period, startDate, asin, fail]);

  useEffect(() => { load(); }, [load]);

  function body() {
    if (checking) return <div className={s.empty}>Checking connection…</div>;
    if (!connected || notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon</h2>
          <p className={s.lockedBody}>
            Visibility is read from your Amazon seller reports.
          </p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Connect Amazon
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={period}
                        onChange={(e) => { setPeriod(e.target.value); setStartDate(''); }}>
                  {['WEEK', 'MONTH', 'QUARTER'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className={s.field}>
                <span className={s.label}>Window</span>
                <select className={s.select} value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}>
                  {!windowOptions.length && <option value="">No data yet</option>}
                  {windowOptions.map((w) => (
                    <option key={w.start_date} value={w.start_date}>
                      {w.start_date}{w.collected ? '' : ' — not collected'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className={s.note}>
              Read from what Search Terms has already collected — this screen asks Amazon
              for nothing of its own.
            </div>
          </div>
        </section>

        <FeatureCards data={overview} loading={loading && !overview}
                      onOpen={(key) => setTab(key === 'page_views' ? 'sessions' : key)} />

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => setTab(t.id)}>
                {t.label}<span className={s.tabRef}>{t.ref}</span>
              </button>
            ))}
          </div>

          <div style={{ paddingTop: 16 }}>
            {tab === 'keywords' && (
              <>
                <div className={s.controls} style={{ marginBottom: 14 }}>
                  <label className={s.field} style={{ minWidth: 280 }}>
                    <span className={s.label}>Product</span>
                    <select className={s.select} value={asin}
                            onChange={(e) => setAsin(e.target.value)}>
                      <option value="">Choose a product…</option>
                      {catalogue.map((p) => (
                        <option key={p.asin} value={p.asin}>
                          {p.asin}{p.title ? ` — ${p.title.slice(0, 60)}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  {catalogue.length > 0 && (
                    <span className={s.hint}>{num(catalogue.length)} products in your catalogue</span>
                  )}
                </div>
                <Keywords data={keywords} asin={asin} />
              </>
            )}
            {tab === 'sessions' && (
              <>
                <Traffic data={traffic} />
                <ProductTraffic data={series} loading={seriesLoading}
                                onLoad={async (value) => {
                                  setSeriesLoading(true);
                                  try {
                                    setSeries(await getProductTraffic(token, {
                                      asin: value, period }));
                                  } catch (e) { fail(e); } finally { setSeriesLoading(false); }
                                }} />
              </>
            )}
            {tab === 'score' && <Score data={scores} />}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Organic Ranking &amp; Visibility Tracker</h1></div>
          <p>How visible your products are in Amazon search, and where that is won or lost</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
